import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import itemDetailImage from 'src/assets/gift/item-detail-image.png';

import {
  giftStoreNavBarScrolledAtom,
  giftStoreNavBarTitleAtom,
  giftStoreNavBarTransparentAtom,
} from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { giftCartAtom, GiftCartItem } from '../state/giftCart.atom';

const GiftItemDetailPage: FC = function GiftItemDetailPage() {
  const { itemId } = useParams<{ itemId?: string }>();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');
  const navigate = useNavigate();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const setTransparent = useSetRecoilState(giftStoreNavBarTransparentAtom);
  const setScrolled = useSetRecoilState(giftStoreNavBarScrolledAtom);
  const setCartItems = useSetRecoilState(giftCartAtom);

  // 투명 모드 활성화 (마운트 시)
  useEffect(() => {
    setTitle(' ');
    setTransparent(true);
    setScrolled(false);

    return () => {
      setTransparent(false);
      setScrolled(false);
    };
  }, [setTitle, setTransparent, setScrolled]);

  useEffect(() => {
    if (bugoId && itemId) trackGiftEvent('item_view', bugoId, itemId);
  }, [bugoId, itemId]);
  // 상품 조회
  useGiftItemForStoreHook(itemId ?? '');
  const status = useRecoilValue(giftItemForStoreStatusAtomFamily(itemId ?? ''));
  const item = useRecoilValue(giftItemForStoreDataAtomFamily(itemId ?? ''));

  // Carousel 상태
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 수량 상태
  const [quantity, setQuantity] = useState(1);

  // 최소주문수량, 주문단위
  const minOrderQuantity = useMemo(() => {
    return item?.giftItemInfo?.minOrderQuantity || 1;
  }, [item]);

  const orderUnit = useMemo(() => {
    return item?.giftItemInfo?.orderUnit || 1;
  }, [item]);

  // 초기 수량 설정
  useEffect(() => {
    if (minOrderQuantity > 0) {
      setQuantity(minOrderQuantity);
    }
  }, [minOrderQuantity]);

  // 상품명 (오버라이드 우선)
  const productName = useMemo(() => {
    return item?.name || item?.giftItemInfo?.name || '';
  }, [item]);

  // 스크롤 감지 - 이미지 절반 지점에서 헤더 스타일 변경
  useEffect(() => {
    const handleScroll = () => {
      // 이미지는 aspect-square이므로 화면 너비 = 이미지 높이
      const imageHeight = window.innerWidth;
      const scrollThreshold = imageHeight / 2;
      const isScrolledPast = window.scrollY > scrollThreshold;

      setScrolled(isScrolledPast);

      // 스크롤 후에는 상품명을 타이틀로 표시
      if (isScrolledPast && productName) {
        setTitle(productName);
      } else {
        setTitle(' ');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrolled, setTitle, productName]);

  // 헤더 이미지 (오버라이드 우선 + choiceUnit 이미지 병합)
  const baseHeaderImages = useMemo(() => {
    if (item?.headerImages && item.headerImages.length > 0) {
      return item.headerImages as Image[];
    }
    if (item?.giftItemInfo?.headerImages && item.giftItemInfo.headerImages.length > 0) {
      return item.giftItemInfo.headerImages as Image[];
    }
    const mainImage = item?.image || item?.giftItemInfo?.image;
    if (mainImage && typeof mainImage === 'object') {
      return [mainImage as Image];
    }
    return [];
  }, [item]);

  // 상세 이미지 (오버라이드 우선)
  const detailImages = useMemo(() => {
    if (item?.detailImages && item.detailImages.length > 0) {
      return item.detailImages as Image[];
    }
    if (item?.giftItemInfo?.detailImages && item.giftItemInfo.detailImages.length > 0) {
      return item.giftItemInfo.detailImages as Image[];
    }
    return [];
  }, [item]);

  // 상품 타입
  const productType = useMemo(() => {
    return item?.giftItemInfo?.type || GiftItemInfoType.Delivery;
  }, [item]);

  // 쿠폰 상세
  const couponDetail = useMemo(() => {
    return item?.giftItemInfo?.couponDetail;
  }, [item]);

  // 브랜드 이름
  const brandName = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return null;
  }, [item]);

  // 선택형 상품 여부 및 선택 옵션
  const isSelectable = useMemo(() => {
    return item?.giftItemInfo?.isSelectable || false;
  }, [item]);

  const choiceUnits = useMemo(() => {
    const units = item?.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return [];
    return units.filter(
      (unit): unit is GiftChoiceUnitData => typeof unit === 'object' && unit !== null,
    );
  }, [item]);

  // choiceUnits에서 이미지가 있는 항목만 추출
  const choiceUnitImages = useMemo(() => {
    if (!isSelectable || choiceUnits.length === 0) return [];
    return choiceUnits
      .filter((unit) => unit.image && typeof unit.image === 'object')
      .map((unit) => unit.image as Image);
  }, [isSelectable, choiceUnits]);

  // headerImages + choiceUnit 이미지 병합
  const headerImages = useMemo(() => {
    if (choiceUnitImages.length === 0) return baseHeaderImages;
    const existingUrls = new Set(baseHeaderImages.map((img) => img.url));
    const uniqueChoiceImages = choiceUnitImages.filter(
      (img) => !existingUrls.has(img.url),
    );
    return [...baseHeaderImages, ...uniqueChoiceImages];
  }, [baseHeaderImages, choiceUnitImages]);

  const choiceUnitImageStartIndex = baseHeaderImages.length;

  // 스와이프 감지 (최소 50px 이동)
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setCurrentImageIndex((prev) => (prev === headerImages.length - 1 ? 0 : prev + 1));
    } else if (isRightSwipe) {
      setCurrentImageIndex((prev) => (prev === 0 ? headerImages.length - 1 : prev - 1));
    }
  }, [touchStart, touchEnd, headerImages.length]);

  // 상품 설명 (오버라이드 우선)
  const detailInfo = useMemo(() => {
    return item?.detailInfo || item?.giftItemInfo?.detailInfo || '';
  }, [item]);

  // 가격
  const priceRegular = item?.priceRegular || 0;
  const priceRetail = item?.priceRetail || 0;
  const discountRate =
    priceRegular > 0
      ? Math.round(((priceRegular - priceRetail) / priceRegular) * 100)
      : 0;

  // 수량 증가
  const increaseQuantity = useCallback(() => {
    setQuantity((prev) => prev + orderUnit);
  }, [orderUnit]);

  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    setQuantity((prev) => {
      const newQty = prev - orderUnit;
      return newQty >= minOrderQuantity ? newQty : prev;
    });
  }, [orderUnit, minOrderQuantity]);

  // 수량 직접 입력
  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setQuantity(value);
    }
  }, []);

  // 입력 완료 시 최소수량/주문단위 조정
  const handleQuantityBlur = useCallback(() => {
    setQuantity((prev) => {
      // 최소주문수량 이하면 최소주문수량으로
      if (prev < minOrderQuantity) {
        return minOrderQuantity;
      }
      // 주문단위에 맞게 조정 (올림)
      if (orderUnit > 1) {
        const remainder = (prev - minOrderQuantity) % orderUnit;
        if (remainder !== 0) {
          return prev + (orderUnit - remainder);
        }
      }
      return prev;
    });
  }, [minOrderQuantity, orderUnit]);

  // 대표 이미지 URL
  const mainImageUrl = useMemo(() => {
    const img = item?.image || item?.giftItemInfo?.image;
    if (img && typeof img === 'object' && 'url' in img) {
      return (img as Image).url;
    }
    return '';
  }, [item]);

  // 장바구니 담기
  const handleAddToCart = useCallback(() => {
    if (!itemId || !bugoId) return;

    setCartItems((prev) => {
      // 같은 상품이 있는지 확인
      const existingItem = prev.find((cartItem) => cartItem.itemId === itemId);

      if (existingItem) {
        // 있으면 수량 업데이트
        return prev.map((cartItem) =>
          cartItem.itemId === itemId ? { ...cartItem, quantity } : cartItem,
        );
      } else {
        // 없으면 새로 추가
        const newCartItem: GiftCartItem = {
          itemId: itemId,
          itemName: productName,
          itemImage: mainImageUrl,
          priceRetail,
          quantity,
          minOrderQuantity,
          orderUnit,
          bugoId: bugoId,
        };
        return [...prev, newCartItem];
      }
    });

    alert(`'${productName}' ${quantity}개가 장바구니에 담겼습니다.`);
  }, [
    itemId,
    bugoId,
    productName,
    mainImageUrl,
    priceRetail,
    quantity,
    minOrderQuantity,
    orderUnit,
    setCartItems,
  ]);

  // 바로 구매
  const handleBuyNow = useCallback(() => {
    navigate(`/gift-store/item/${itemId}/checkout?bugoId=${bugoId}&quantity=${quantity}`);
  }, [navigate, itemId, bugoId, quantity]);

  // 총 금액
  const totalPrice = priceRetail * quantity;

  if (status !== 'success' || !item) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-26">
      {/* 헤더 이미지 Carousel */}
      {headerImages.length > 0 && (
        <div className="relative">
          <div
            className="aspect-square w-full overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex h-full transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {headerImages.map((img, idx) => {
                const isChoiceUnitImg = idx >= choiceUnitImageStartIndex;
                const choiceUnit = isChoiceUnitImg
                  ? choiceUnits.find(
                      (u) =>
                        u.image &&
                        typeof u.image === 'object' &&
                        (u.image as Image).url === img.url,
                    )
                  : null;

                return (
                  <div key={idx} className="relative h-full w-full flex-shrink-0">
                    <img
                      src={fileBucketUrl(img?.url ?? '', 'raw')}
                      alt={`${productName} ${idx + 1}`}
                      className="h-full w-full object-cover"
                    />
                    {isChoiceUnitImg && choiceUnit && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-4 pb-4 pt-10">
                        <span className="inline-block rounded-full bg-indigo-500 px-2.5 py-0.5 text-xs font-bold text-white">
                          선택옵션
                        </span>
                        <p className="mt-1 text-base font-bold text-white">
                          {choiceUnit.name}
                        </p>
                        {choiceUnit.priceRegular && (
                          <p className="text-sm text-white/80">
                            정가 {formatPrice(choiceUnit.priceRegular)}원
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carousel 네비게이션 */}
          {headerImages.length > 1 && (
            <>
              {/* 좌우 버튼 */}
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === 0 ? headerImages.length - 1 : prev - 1,
                  )
                }
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ◀
              </button>
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === headerImages.length - 1 ? 0 : prev + 1,
                  )
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ▶
              </button>

              {/* 인디케이터 */}
              <div className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white">
                {currentImageIndex + 1} / {headerImages.length}
              </div>
            </>
          )}
        </div>
      )}

      {/* 상품 정보 */}
      <div className="p-4">
        {/* 브랜드 & 타입 뱃지 */}
        <div className="flex flex-wrap items-center gap-1.5">
          {isSelectable && (
            <span className="inline-block rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2.5 py-0.5 text-xs font-bold text-white">
              선택형
            </span>
          )}
          {brandName && (
            <span className="inline-block rounded bg-orange-500 px-2 py-0.5 text-xs font-bold text-white">
              {brandName}
            </span>
          )}
          {productType === GiftItemInfoType.Delivery && (
            <span className="inline-block rounded bg-blue-500 px-2 py-0.5 text-xs font-bold text-white">
              배송형
            </span>
          )}
          {productType === GiftItemInfoType.Coupon && (
            <span className="inline-block rounded bg-purple-500 px-2 py-0.5 text-xs font-bold text-white">
              쿠폰형
            </span>
          )}
        </div>

        {/* 상품명 */}
        <h1 className="mt-2 text-xl font-bold">{productName}</h1>

        {/* 가격 */}
        <div className="mt-3 flex items-end gap-2">
          {discountRate > 0 && (
            <>
              <span className="text-2xl font-bold text-red-500">{discountRate}%</span>
              <span className="text-lg text-gray-400 line-through">
                {formatPrice(priceRegular)}원
              </span>
            </>
          )}
          <span className="text-2xl font-bold">{formatPrice(priceRetail)}원</span>
        </div>

        {/* 선택형 상품 안내 배너 */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg">
                🎁
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-800">
                  {choiceUnits.length}가지 옵션 중 선택 가능!
                </p>
                <p className="mt-0.5 text-xs text-indigo-600/80">
                  받으시는 분이 원하는 상품을 직접 선택할 수 있어요
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 쿠폰 정보 (쿠폰형일 때) */}
        {productType === GiftItemInfoType.Coupon && couponDetail && (
          <div className="mt-4 rounded-lg bg-purple-50 p-4">
            <h3 className="font-bold text-purple-700">쿠폰 정보</h3>
            <div className="mt-2 space-y-1 text-sm">
              {couponDetail.validPeriod && (
                <div className="flex">
                  <span className="w-16 text-gray-500">유효기간</span>
                  <span className="font-medium">{couponDetail.validPeriod}</span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div className="flex">
                  <span className="w-16 text-gray-500">사용처</span>
                  <span className="font-medium">{couponDetail.placeOfExchange}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 선택 가능 옵션 (선택형일 때) */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-indigo-100">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
              <h3 className="text-sm font-bold text-white">
                선택 가능한 옵션 ({choiceUnits.length}종)
              </h3>
              <p className="mt-0.5 text-xs text-white/80">
                받으시는 분이 아래 중 하나를 선택합니다
              </p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 p-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {choiceUnits.map((unit, unitIdx) => {
                  const unitBrandName =
                    unit.giftItemBrand &&
                    typeof unit.giftItemBrand === 'object' &&
                    'name' in unit.giftItemBrand
                      ? unit.giftItemBrand.name
                      : null;

                  return (
                    <div
                      key={unit._id}
                      className="flex w-28 flex-shrink-0 flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
                    >
                      <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                        {unit.image ? (
                          <img
                            src={fileBucketUrl(
                              typeof unit.image === 'object'
                                ? unit.image.url
                                : unit.image,
                              'w200',
                            )}
                            alt={unit.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                            No Image
                          </div>
                        )}
                        <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {unitIdx + 1}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-2">
                        {unitBrandName && (
                          <span className="mb-1 self-start rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">
                            {unitBrandName}
                          </span>
                        )}
                        <span className="line-clamp-2 text-xs font-medium text-gray-800">
                          {unit.name}
                        </span>
                        {unit.priceRegular && (
                          <span className="mt-auto pt-1 text-xs font-bold text-indigo-600">
                            {formatPrice(unit.priceRegular)}원
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 상품 설명 */}
        {detailInfo && (
          <div className="mt-6">
            <h3 className="font-bold text-gray-700">상품 설명</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{detailInfo}</p>
          </div>
        )}

        {/* 주문 정보 */}
        <div className="mt-6 rounded-lg bg-gray-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">최소 주문수량</span>
            <span className="font-medium">{minOrderQuantity}개</span>
          </div>
          {orderUnit > 1 && (
            <div className="mt-1 flex justify-between">
              <span className="text-gray-500">주문 단위</span>
              <span className="font-medium">{orderUnit}개씩</span>
            </div>
          )}
        </div>
      </div>

      {/* 선택형 상품 설명 이미지 (쿠폰형 + 선택형일 때) */}
      {productType === GiftItemInfoType.Coupon && isSelectable && (
        <div className="border-t">
          <img src={itemDetailImage} alt="선택형 답례품 안내" className="w-full" />
        </div>
      )}

      {/* 선택 옵션별 상세 이미지 */}
      {isSelectable && choiceUnits.length > 0 && (
        <div className="border-t">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
            <h3 className="text-base font-bold text-white">
              선택 가능한 옵션 상세 ({choiceUnits.length}종)
            </h3>
          </div>
          {choiceUnits.map((unit) => {
            const unitBrandName =
              unit.giftItemBrand &&
              typeof unit.giftItemBrand === 'object' &&
              'name' in unit.giftItemBrand
                ? unit.giftItemBrand.name
                : null;

            return (
              <div key={unit._id} className="border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-3 bg-gray-50 px-4 py-3">
                  {unit.image && typeof unit.image === 'object' && (
                    <img
                      src={fileBucketUrl((unit.image as Image).url, 'w200')}
                      alt={unit.name}
                      className="h-12 w-12 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      {unitBrandName && (
                        <span className="break-keep rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">
                          {unitBrandName}
                        </span>
                      )}
                      <span className="break-keep text-sm font-bold text-gray-800">
                        {unit.name}
                      </span>
                    </div>
                    {unit.priceRegular && (
                      <span className="text-xs text-gray-500">
                        정가 {formatPrice(unit.priceRegular)}원
                      </span>
                    )}
                  </div>
                </div>
                {unit.description && (
                  <div className="bg-white px-4 py-3">
                    <p className="whitespace-pre-wrap break-keep text-sm leading-relaxed text-gray-600">
                      {unit.description}
                    </p>
                  </div>
                )}
                {unit.image && typeof unit.image === 'object' && (
                  <img
                    src={fileBucketUrl((unit.image as Image).url, 'raw')}
                    alt={`${unit.name} 상세`}
                    className="w-full"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 상세 이미지 */}
      {detailImages.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <h3 className="px-4 font-bold text-gray-700">상세 정보</h3>
          <div className="mt-2">
            {detailImages.map((img, idx) => (
              <img
                key={`detail-${idx}`}
                src={fileBucketUrl(img.url, 'raw')}
                alt={`상세이미지 ${idx + 1}`}
                className="w-full"
              />
            ))}
          </div>
        </div>
      )}

      {/* 하단 고정 영역 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white px-3 py-2 shadow-lg">
        <div className="mx-auto max-w-lg space-y-2">
          {/* 수량 조절 (상단 한 줄) */}
          <div className="relative flex items-center justify-center">
            <span className="absolute left-2 text-sm text-gray-600">수량</span>
            <div className="flex items-center">
              <button
                onClick={decreaseQuantity}
                disabled={quantity <= minOrderQuantity}
                className="flex h-10 w-14 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-2xl font-bold text-gray-700 active:bg-gray-300 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-300"
              >
                −
              </button>
              <input
                type="number"
                inputMode="numeric"
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                className="w-[72px] bg-transparent text-center text-lg font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                onClick={increaseQuantity}
                className="flex h-10 w-14 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-2xl font-bold text-gray-700 active:bg-gray-300"
              >
                +
              </button>
            </div>
            {orderUnit > 1 && (
              <span className="absolute right-2 text-xs text-gray-400">
                ({orderUnit}개 단위)
              </span>
            )}
          </div>

          {/* 하단: 총금액 + 버튼들 */}
          <div className="flex items-center gap-2">
            {/* 총 금액 */}
            <div className="flex-1">
              <span className="text-xs text-gray-500">총 금액</span>
              <p className="text-base font-bold text-red-500">
                {formatPrice(totalPrice)}원
              </p>
            </div>

            {/* 버튼들 */}
            <button
              onClick={handleAddToCart}
              className="rounded-lg border-2 border-gray-800 px-5 py-2.5 text-base font-bold text-gray-800 active:bg-gray-100"
            >
              장바구니
            </button>
            <button
              onClick={handleBuyNow}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-base font-bold text-white active:bg-red-600"
            >
              바로구매
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftItemDetailPage;
